/**
 * Epic FHIR R4 Client (JavaScript)
 *
 * SMART on FHIR OAuth2 + PKCE for Epic sandbox integration.
 * Ported from fhirClient.ts for React frontend use.
 *
 * Patent Pending: U.S. Provisional Application Nos. 63/939,190,
 *   63/962,385 & 63/988,485
 * Copyright 2025-2026 Nexus Concordat Inc.
 */

// ============================================================
// Configuration
// ============================================================

const EPIC_SANDBOX_URL = 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4';
const CLIENT_ID = '79817839-b713-4133-a8e0-5d72e5a7c420';
const REDIRECT_URI = window.location.origin + '/discharge-demo/callback';

// ============================================================
// PKCE Utilities
// ============================================================

function generateRandomString(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (v) => chars[v % chars.length]).join('');
}

async function sha256(plain) {
  const encoder = new TextEncoder();
  return crypto.subtle.digest('SHA-256', encoder.encode(plain));
}

function base64urlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generatePKCE() {
  const verifier = generateRandomString(64);
  const hashed = await sha256(verifier);
  const challenge = base64urlEncode(hashed);
  return { verifier, challenge };
}

// ============================================================
// SMART Discovery
// ============================================================

let smartConfig = null;

async function discoverSmartConfig() {
  if (smartConfig) return smartConfig;
  const url = `${EPIC_SANDBOX_URL}/.well-known/smart-configuration`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`SMART discovery failed: ${resp.status}`);
  }
  smartConfig = await resp.json();
  return smartConfig;
}

// ============================================================
// OAuth2 Flow
// ============================================================

export async function startEpicAuth() {
  const config = await discoverSmartConfig();
  const pkce = await generatePKCE();

  sessionStorage.setItem('nexus_health_pkce_verifier', pkce.verifier);

  const state = generateRandomString(32);
  sessionStorage.setItem('nexus_health_oauth_state', state);

  const scopes = [
    'launch/patient',
    'patient/Patient.rs',
    'patient/Encounter.rs',
    'patient/Condition.rs',
    'patient/MedicationRequest.rs',
    'patient/Observation.rs',
    'patient/Coverage.rs',
    'openid',
    'fhirUser',
  ];

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: scopes.join(' '),
    state,
    aud: EPIC_SANDBOX_URL,
    code_challenge: pkce.challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `${config.authorization_endpoint}?${params.toString()}`;
}

export async function handleEpicCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');

  if (error) {
    throw new Error(`Epic auth error: ${error} - ${urlParams.get('error_description') || ''}`);
  }

  if (!code || !state) {
    return null; // Not a callback
  }

  const savedState = sessionStorage.getItem('nexus_health_oauth_state');
  if (state !== savedState) {
    throw new Error('OAuth state mismatch. Possible CSRF attack.');
  }

  const verifier = sessionStorage.getItem('nexus_health_pkce_verifier');
  if (!verifier) {
    throw new Error('PKCE verifier not found. Restart authorization.');
  }

  const config = await discoverSmartConfig();

  const resp = await fetch(config.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: REDIRECT_URI,
      client_id: CLIENT_ID,
      code_verifier: verifier,
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Token exchange failed: ${resp.status} ${errText}`);
  }

  const token = await resp.json();

  // Store token
  sessionStorage.setItem('nexus_health_access_token', token.access_token);
  sessionStorage.setItem('nexus_health_patient_id', token.patient || '');
  sessionStorage.setItem('nexus_health_token_expiry', String(Date.now() + token.expires_in * 1000));

  // Clean up
  sessionStorage.removeItem('nexus_health_pkce_verifier');
  sessionStorage.removeItem('nexus_health_oauth_state');

  return token;
}

// ============================================================
// FHIR Queries
// ============================================================

function getAccessToken() {
  const token = sessionStorage.getItem('nexus_health_access_token');
  const expiry = sessionStorage.getItem('nexus_health_token_expiry');
  if (!token || !expiry || Date.now() >= Number(expiry) - 60000) {
    return null;
  }
  return token;
}

export function isConnected() {
  return getAccessToken() !== null;
}

export function disconnect() {
  sessionStorage.removeItem('nexus_health_access_token');
  sessionStorage.removeItem('nexus_health_patient_id');
  sessionStorage.removeItem('nexus_health_token_expiry');
}

async function fhirFetch(path) {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  const url = path.startsWith('http') ? path : `${EPIC_SANDBOX_URL}/${path}`;
  const resp = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/fhir+json',
    },
  });

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`FHIR ${path}: ${resp.status} ${errText}`);
  }

  return resp.json();
}

// ============================================================
// Patient Data Extraction
// ============================================================

export async function fetchPatientContext(patientId) {
  const id = patientId || sessionStorage.getItem('nexus_health_patient_id');
  if (!id) throw new Error('No patient ID from Epic context');

  // Fetch patient + conditions + encounters + meds + coverage in parallel
  const [patient, conditions, encounters, medications, coverage] = await Promise.all([
    fhirFetch(`Patient/${id}`),
    fhirFetch(`Condition?patient=${id}&clinical-status=active&_count=20`).catch(() => ({ entry: [] })),
    fhirFetch(`Encounter?patient=${id}&status=in-progress&_sort=-date&_count=5`).catch(() => ({ entry: [] })),
    fhirFetch(`MedicationRequest?patient=${id}&status=active&_count=20`).catch(() => ({ entry: [] })),
    fhirFetch(`Coverage?beneficiary=${id}&status=active`).catch(() => ({ entry: [] })),
  ]);

  return { patient, conditions, encounters, medications, coverage };
}

export async function searchPatients(name) {
  const params = new URLSearchParams({ name, _count: '10' });
  return fhirFetch(`Patient?${params.toString()}`);
}

// ============================================================
// FHIR to Discharge Format Converter
// ============================================================

function calculateAge(birthDate) {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function extractInsurance(coverageBundle) {
  const entries = coverageBundle?.entry || [];
  if (entries.length === 0) return 'private';

  const cov = entries[0].resource;
  const payorName = cov?.payor?.[0]?.display?.toLowerCase() || '';
  // eslint-disable-next-line no-unused-vars
  const className = cov?.class?.[0]?.value?.toLowerCase() || '';
  const typeCode = cov?.type?.coding?.[0]?.code?.toLowerCase() || '';

  if (payorName.includes('medicare') || typeCode.includes('medicare')) return 'medicare';
  if (payorName.includes('medicaid') || typeCode.includes('medicaid')) return 'medicaid';
  return 'private';
}

function extractDiagnosis(conditionBundle) {
  const entries = conditionBundle?.entry || [];
  if (entries.length === 0) return { code: 'R69', label: 'Illness, unspecified' };

  const condition = entries[0].resource;
  const coding = condition?.code?.coding?.[0] || {};
  return {
    code: coding.code || 'R69',
    label: coding.display || condition?.code?.text || 'Unspecified condition',
  };
}

function extractBarriers(conditionBundle, medicationBundle) {
  const barriers = [];
  const entries = conditionBundle?.entry || [];
  const meds = medicationBundle?.entry || [];

  for (const entry of entries) {
    const cond = entry.resource;
    const code = cond?.code?.coding?.[0]?.code || '';
    const display = (cond?.code?.text || cond?.code?.coding?.[0]?.display || '').toLowerCase();

    // Behavioral health
    if (code.startsWith('F') || display.includes('depress') || display.includes('anxiety') ||
        display.includes('psycho') || display.includes('bipolar')) {
      if (!barriers.includes('behavioral_health')) barriers.push('behavioral_health');
    }
    // Cognitive
    if (display.includes('dementia') || display.includes('cognitive') || display.includes('alzheimer')) {
      if (!barriers.includes('cognitive_impairment')) barriers.push('cognitive_impairment');
    }
    // Wound
    if (display.includes('wound') || display.includes('ulcer') || display.includes('pressure')) {
      if (!barriers.includes('wound_care')) barriers.push('wound_care');
    }
    // Fracture / surgery needing rehab
    if (code.startsWith('S7') || code.startsWith('M17') || display.includes('fracture') ||
        display.includes('replacement') || display.includes('stroke')) {
      if (!barriers.includes('needs_rehab')) barriers.push('needs_rehab');
      if (!barriers.includes('needs_equipment')) barriers.push('needs_equipment');
    }
  }

  // Check for IV meds
  for (const entry of meds) {
    const med = entry.resource;
    const route = med?.dosageInstruction?.[0]?.route?.coding?.[0]?.display?.toLowerCase() || '';
    if (route.includes('intraven') || route.includes('iv')) {
      if (!barriers.includes('iv_therapy')) barriers.push('iv_therapy');
    }
  }

  return barriers;
}

function extractDaysAdmitted(encounterBundle) {
  const entries = encounterBundle?.entry || [];
  if (entries.length === 0) return 1;

  const enc = entries[0].resource;
  const start = enc?.period?.start;
  if (!start) return 1;

  const startDate = new Date(start);
  const now = new Date();
  const days = Math.max(1, Math.ceil((now - startDate) / (1000 * 60 * 60 * 24)));
  return days;
}

function assessCondition(conditionBundle, encounterBundle) {
  const entries = conditionBundle?.entry || [];
  const encEntries = encounterBundle?.entry || [];

  // Check encounter class for ICU
  for (const e of encEntries) {
    const cls = e.resource?.class?.code?.toLowerCase() || '';
    if (cls.includes('emer') || cls.includes('icu')) return 'critical';
  }

  // Count active conditions
  if (entries.length >= 4) return 'complex';
  if (entries.length >= 2) return 'improving';
  return 'stable';
}

export function fhirToDischargePatient(fhirContext) {
  const { patient, conditions, encounters, medications, coverage } = fhirContext;

  const name = patient.name?.[0];
  const displayName = name
    ? `${name.given?.[0] || ''} ${name.family || ''}`.trim()
    : `Patient ${patient.id}`;

  const encEntry = encounters?.entry?.[0]?.resource;
  const unit = encEntry?.serviceType?.coding?.[0]?.display
    || encEntry?.type?.[0]?.coding?.[0]?.display
    || encEntry?.class?.display
    || 'General';

  return {
    id: `EPIC-${patient.id}`,
    name: displayName,
    age: calculateAge(patient.birthDate),
    insurance: extractInsurance(coverage),
    condition: assessCondition(conditions, encounters),
    daysAdmitted: extractDaysAdmitted(encounters),
    diagnosis: extractDiagnosis(conditions),
    barriers: extractBarriers(conditions, medications),
    unit: unit,
    summary: `Epic FHIR patient. ${(conditions?.entry || []).length} active conditions. ${(medications?.entry || []).length} active medications.`,
    epicSource: true,
  };
}
